// src/crashReports/performance-monitor.ts
import { pick } from 'lodash-es';
import type { CrashMonitorConfig, PerformanceMetrics } from '../types';

/**
 * 性能监控器
 */
export class PerformanceMonitor {
  private metrics: PerformanceMetrics | null = null;
  private monitoringInterval: ReturnType<typeof setInterval> | null = null;
  private frameCount = 0;
  private lastFrameTime = performance.now();
  private fps = 0;
  private animationFrameId: number | null = null;

  constructor(private config: Partial<CrashMonitorConfig> = {}) {
    this.lastFrameTime = performance.now();
    // 启动FPS计算
    this.calculateFPS();
  }
  /**
   * 获取内存使用情况
   */
  private getMemoryMetrics() {
    if ('memory' in performance) {
      const memory = (performance as any).memory;
      return {
        usedJSHeapSize: memory.usedJSHeapSize,
        totalJSHeapSize: memory.totalJSHeapSize,
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        memoryUsage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
      };
    }
    return {
      usedJSHeapSize: 0,
      totalJSHeapSize: 0,
      jsHeapSizeLimit: 0,
      memoryUsage: 0,
    };
  }

  /**
   * 获取CPU/性能指标
   */
  private getCPUMetrics() {
    const navigation = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;
    const paint = performance.getEntriesByType('paint');

    let firstPaint = 0;
    let firstContentfulPaint = 0;

    paint.forEach((entry) => {
      if (entry.name === 'first-paint') {
        firstPaint = entry.startTime;
      }
      if (entry.name === 'first-contentful-paint') {
        firstContentfulPaint = entry.startTime;
      }
    });

    // 获取最大内容绘制时间
    const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
    const largestContentfulPaint =
      lcpEntries.length > 0 ? lcpEntries[lcpEntries.length - 1].startTime : 0;

    return {
      loadTime: navigation.loadEventEnd - navigation.loadEventStart,
      domContentLoaded:
        navigation.domContentLoadedEventEnd -
        navigation.domContentLoadedEventStart,
      firstPaint,
      firstContentfulPaint,
      largestContentfulPaint,
    };
  }

  /**
   * 获取网络指标
   */
  private getNetworkMetrics() {
    const navigation = performance.getEntriesByType(
      'navigation'
    )[0] as PerformanceNavigationTiming;

    return {
      // @ts-ignore
      navigationStart: navigation.navigationStart,
      fetchStart: navigation.fetchStart,
      domainLookupStart: navigation.domainLookupStart,
      domainLookupEnd: navigation.domainLookupEnd,
      connectStart: navigation.connectStart,
      connectEnd: navigation.connectEnd,
      requestStart: navigation.requestStart,
      responseStart: navigation.responseStart,
      responseEnd: navigation.responseEnd,
      // @ts-ignore
      domLoading: navigation.domLoading,
      domInteractive: navigation.domInteractive,
      domContentLoadedEventStart: navigation.domContentLoadedEventStart,
      domContentLoadedEventEnd: navigation.domContentLoadedEventEnd,
      domComplete: navigation.domComplete,
      loadEventStart: navigation.loadEventStart,
      loadEventEnd: navigation.loadEventEnd,
    };
  }

  /**
   * 获取资源指标
   */
  private getResourceMetrics() {
    const resources = performance.getEntriesByType(
      'resource'
    ) as PerformanceResourceTiming[];

    const totalSize = resources.reduce((sum, resource) => {
      return sum + (resource.transferSize || 0);
    }, 0);

    const totalLoadTime = resources.reduce((sum, resource) => {
      // @ts-ignore
      return sum + (resource.loadEventEnd - resource.loadEventStart);
    }, 0);

    return {
      totalResources: resources.length,
      totalSize,
      averageLoadTime:
        resources.length > 0 ? totalLoadTime / resources.length : 0,
    };
  }

  /**
   * 获取运行时指标
   */
  private getRuntimeMetrics() {
    const now = performance.now();

    return {
      timestamp: Date.now(),
      uptime: now,
      frameRate: this.fps,
      activeConnections: navigator.connection
        ? (navigator.connection as any).effectiveType
        : 'unknown',
    };
  }

  /**
   * 使用requestAnimationFrame计算FPS
   */
  private calculateFPS() {
    const now = performance.now();
    this.frameCount++;

    if (now - this.lastFrameTime >= 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.lastFrameTime = now;
    }

    this.animationFrameId = requestAnimationFrame(() => this.calculateFPS());
  }

  /**
   * 获取完整的性能指标
   */
  getMetrics(groups: string[] = []): Partial<PerformanceMetrics> {
    this.metrics = {
      memory: this.getMemoryMetrics(),
      cpu: this.getCPUMetrics(),
      network: this.getNetworkMetrics(),
      resources: this.getResourceMetrics(),
      runtime: this.getRuntimeMetrics(),
    };
    if (groups.length > 0) {
      return pick(this.metrics, groups) as Partial<PerformanceMetrics>;
    }

    return this.metrics;
  }

  /**
   * 停止监控
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
    // 停止FPS计算
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * 获取内存使用情况的详细信息
   */
  getDetailedMemoryInfo() {
    const memory = this.getMemoryMetrics();

    return {
      ...memory,
      usedJSHeapSizeMB:
        (memory.usedJSHeapSize / 1024 / 1024).toFixed(2) + ' MB',
      totalJSHeapSizeMB:
        (memory.totalJSHeapSize / 1024 / 1024).toFixed(2) + ' MB',
      jsHeapSizeLimitMB:
        (memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2) + ' MB',
      memoryUsageFormatted: memory.memoryUsage.toFixed(2) + '%',
    };
  }

  /**
   * 检查性能问题
   */
  checkPerformanceIssues(): Array<{
    type: string;
    message: string;
    severity: 'low' | 'medium' | 'high';
  }> {
    const metrics = this.getMetrics() as PerformanceMetrics;
    const issues: Array<{
      type: string;
      message: string;
      severity: 'low' | 'medium' | 'high';
    }> = [];

    // 检查内存使用率
    if (metrics.memory.memoryUsage > 90) {
      issues.push({
        type: 'memory',
        message: `内存使用率过高: ${metrics.memory.memoryUsage.toFixed(2)}%`,
        severity: 'high',
      });
    } else if (metrics.memory.memoryUsage > 80) {
      issues.push({
        type: 'memory',
        message: `内存使用率较高: ${metrics.memory.memoryUsage.toFixed(2)}%`,
        severity: 'medium',
      });
    }

    // 检查帧率
    if (metrics.runtime.frameRate < 20) {
      issues.push({
        type: 'performance',
        message: `帧率过低: ${metrics.runtime.frameRate} FPS`,
        severity: 'high',
      });
    } else if (metrics.runtime.frameRate < 30) {
      issues.push({
        type: 'performance',
        message: `帧率较低: ${metrics.runtime.frameRate} FPS`,
        severity: 'medium',
      });
    }

    // 检查页面加载时间
    if (metrics.cpu.loadTime > 5000) {
      issues.push({
        type: 'loading',
        message: `页面加载时间过长: ${metrics.cpu.loadTime.toFixed(0)}ms`,
        severity: 'medium',
      });
    }

    return issues;
  }

  /**
   * 导出性能报告
   */
  exportReport(): string {
    const metrics = this.getMetrics();
    const issues = this.checkPerformanceIssues();

    return JSON.stringify(
      {
        timestamp: new Date().toISOString(),
        metrics,
        issues,
        summary: {
          totalIssues: issues.length,
          highSeverityIssues: issues.filter((i) => i.severity === 'high')
            .length,
          mediumSeverityIssues: issues.filter((i) => i.severity === 'medium')
            .length,
          lowSeverityIssues: issues.filter((i) => i.severity === 'low').length,
        },
      },
      null,
      2
    );
  }
}
